import React from 'react';
import {
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Typography,
    Button,
    Chip,
    Box,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    ShoppingCart,
    Favorite,
    FavoriteBorder,
    Inventory,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const ProductCard = ({ product, onFavoriteToggle, isFavorite = false }) => {
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const { user } = useAuth();
    const isOwnListing = user && product.sellerId && String(user.id) === String(product.sellerId);

    const handleCardClick = () => {
        navigate(`/parts/${product.componentId || product.id}/${encodeURIComponent(product.partNumber || product.mpn)}`, {
            state: { part: product }
        });
    };

    const handleAddToCart = (e) => {
        e.stopPropagation();
        const moq = product.minimumOrderQuantity || product.minimum_order_quantity || 1;
        addToCart(product, moq);
    };

    const handleFavoriteClick = (e) => {
        e.stopPropagation();
        if (onFavoriteToggle) {
            onFavoriteToggle(product);
        }
    };

    const displayPrice = product.formattedPrice ||
        (product.unitPrice ? `$${product.unitPrice.toFixed(2)}` :
            (product.price ? `$${product.price.toFixed(2)}` : 'Call for price'));

    const stockStatus = product.inStock ||
        (product.availability > 0) ||
        (product.available_quantity > 0) ||
        (product.status === 'active');

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                },
            }}
            onClick={handleCardClick}
        >
            {/* Image Section */}
            <CardMedia
                component="div"
                sx={{
                    height: 180,
                    bgcolor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    borderBottom: '1px solid #e0e0e0',
                }}
            >
                {product.thumbnailUrl || product.imageUrl ? (
                    <img
                        src={product.thumbnailUrl || product.imageUrl}
                        alt={product.displayName || product.partNumber || product.mpn}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            padding: '8px',
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                ) : (
                    <Inventory sx={{ fontSize: 64, color: 'text.secondary' }} />
                )}

                {/* Category Badge */}
                {product.category && (
                    <Chip
                        label={product.category}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 'bold',
                        }}
                    />
                )}

                {/* Favorite Button */}
                <IconButton
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'white',
                        '&:hover': { bgcolor: 'grey.100' },
                    }}
                    onClick={handleFavoriteClick}
                >
                    {isFavorite ? (
                        <Favorite sx={{ color: 'error.main' }} />
                    ) : (
                        <FavoriteBorder />
                    )}
                </IconButton>

                {/* Condition Badge */}
                {product.condition && product.condition !== 'new' && (
                    <Chip
                        label={product.condition.toUpperCase()}
                        size="small"
                        color="warning"
                        sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                        }}
                    />
                )}
            </CardMedia>

            {/* Content Section */}
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                {/* Part Number */}
                <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {product.displayName || product.partNumber || product.mpn || 'N/A'}
                </Typography>

                {/* Manufacturer */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontSize: '0.85rem', fontWeight: 500 }}
                >
                    {product.manufacturer || 'Unknown'}
                </Typography>

                {/* Description */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        fontSize: '0.75rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: '2.5em',
                        mb: 2,
                    }}
                >
                    {product.shortDescription || product.description || 'No description available'}
                </Typography>

                {/* Price and Stock */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    <Typography
                        variant="h6"
                        color="primary.main"
                        fontWeight="bold"
                        sx={{ fontSize: '1.1rem' }}
                    >
                        {displayPrice}
                    </Typography>

                    <Chip
                        label={stockStatus ? 'In Stock' : 'Out of Stock'}
                        size="small"
                        color={stockStatus ? 'success' : 'error'}
                        variant="outlined"
                    />
                </Box>

                {/* Package Info */}
                {(product.package || product.packaging) && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Package: {product.package || product.packaging}
                    </Typography>
                )}

                {/* MOQ hint */}
                {(product.minimumOrderQuantity || product.minimum_order_quantity) > 1 && (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5, fontWeight: 600 }}>
                        Min. order: {product.minimumOrderQuantity || product.minimum_order_quantity} pcs
                    </Typography>
                )}
            </CardContent>

            {/* Actions */}
            <CardActions sx={{ p: 2, pt: 0 }}>
                <Tooltip title={stockStatus ? 'Add to cart' : 'Out of stock'}>
                    <span style={{ width: '100%' }}>
                        <Button
                            variant="contained"
                            fullWidth
                            size="medium"
                            startIcon={<ShoppingCart />}
                            onClick={handleAddToCart}
                            disabled={isOwnListing || !stockStatus}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 'bold',
                            }}
                        >
                            {isOwnListing ? 'Your Listing' : (stockStatus ? 'Add to Cart' : 'Out of Stock')}
                        </Button>
                    </span>
                </Tooltip>
            </CardActions>
        </Card>
    );
};

export default ProductCard;
